/**
 * Flood fill implementation.
 * @module
 */

interface FloodFillParams {
  imgData: Uint8Array | Uint8ClampedArray;
  width: number;
  height: number;
  numChannels: number;
  startX: number;
  startY: number;
  fillColor: Uint8Array;
  wrapAround: boolean;
}

export function floodFill({
  imgData,
  width,
  height,
  numChannels,
  startX,
  startY,
  fillColor,
  wrapAround,
}: FloodFillParams) {
  console.assert(imgData.length === width * height * numChannels);

  if (startX < 0 || startY < 0 || startX >= width || startY >= height) {
    return;
  }

  const colorToCover = new Uint8Array(numChannels);
  for (let c = 0; c < numChannels; ++c) {
    colorToCover[c] = imgData[(startY * width + startX) * numChannels + c];
  }

  let alreadyFilled = true;
  for (let c = 0; c < numChannels; ++c) {
    if (fillColor[c] !== colorToCover[c]) {
      alreadyFilled = false;
      break;
    }
  }
  if (alreadyFilled) {
    return;
  }

  function shouldCoverPixel(x, y) {
    for (let c = 0; c < numChannels; ++c) {
      const wrappedX = (x + width) % width;
      if (imgData[(y * width + wrappedX) * numChannels + c] !== colorToCover[c]) {
        return false;
      }
    }
    return true;
  }
  // Algorithm: fill a column from top down, and record neighboring column segments that haven't been filled yet.
  const pixelStack = new Int16Array(height * 8);
  let pixelStackLen = 1;
  pixelStack[0] = startX;
  pixelStack[1] = startY;

  while (pixelStackLen > 0) {
    --pixelStackLen;
    const x = (pixelStack[pixelStackLen * 2] + width) % width;
    let y = pixelStack[pixelStackLen * 2 + 1];
    // Find the top of the column
    while (y > 0 && shouldCoverPixel(x, y - 1)) {
      --y;
    }
    if (wrapAround && y === 0 && shouldCoverPixel(x, height - 1)) {
      // Wrap around the top
      pixelStack[pixelStackLen * 2] = x;
      pixelStack[pixelStackLen * 2 + 1] = height - 1;
      ++pixelStackLen;
    }
    // Go down, fill this column, and add neighboring columns to stack
    let addLeftColumn = true;
    let addRightColumn = true;
    while (y < height && shouldCoverPixel(x, y)) {
      if (x > 0 || wrapAround) {
        const shouldCoverLeft = shouldCoverPixel(x - 1, y);
        if (shouldCoverLeft && addLeftColumn) {
          pixelStack[pixelStackLen * 2] = x - 1;
          pixelStack[pixelStackLen * 2 + 1] = y;
          ++pixelStackLen;

          // Don't add this column again until we find a pixel of a different color
          addLeftColumn = false;
        }
        if (!shouldCoverLeft) {
          addLeftColumn = true;
        }
      }
      if (x < width - 1 || wrapAround) {
        const shouldCoverRight = shouldCoverPixel(x + 1, y);
        if (shouldCoverRight && addRightColumn) {
          pixelStack[pixelStackLen * 2] = x + 1;
          pixelStack[pixelStackLen * 2 + 1] = y;
          ++pixelStackLen;

          // Don't add this column again until we find a pixel of a different color
          addRightColumn = false;
        }
        if (!shouldCoverRight) {
          addRightColumn = true;
        }
      }

      for (let c = 0; c < numChannels; ++c) {
        imgData[(y * width + x) * numChannels + c] = fillColor[c];
      }
      ++y;
      if (wrapAround && y === height && shouldCoverPixel(x, 0)) {
        // Wrap around the bottom
        pixelStack[pixelStackLen * 2] = x;
        pixelStack[pixelStackLen * 2 + 1] = 0;
        ++pixelStackLen;
      }
    }
  }
}
